import { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  ArrowLeft, User, CreditCard, FileText, Plus, Trash2, Pencil, KeyRound, Gift,
} from "lucide-react"

import * as api from "@/api/cogeser"
import { useAuth } from "@/hooks/useAuth"
import { usePermissions } from "@/hooks/usePermissions"
import type { ICustomer, ILoyaltyCard, ILoyaltyTransaction, IContract } from "@/types"

// ----------------------------------------------------------------------------

type Tab = "account-loyalty" | "contracts"

export default function CustomerDetail() {
  // Hooks — the :id param is a User ID (from the app-users list)
  const { id: userId } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { token } = useAuth()
  const [activeTab, setActiveTab] = useState<Tab>("account-loyalty")

  // Fetch the User
  const { data: appUser, isLoading: isLoadingUser } = useQuery({
    queryKey: ["user", userId],
    queryFn: () => api.getUser(userId!, token!) as Promise<Record<string, any>>,
    enabled: !!token && !!userId,
  })

  // Fetch Customers linked to this User
  const { data: customersData, isLoading: isLoadingCustomers } = useQuery({
    queryKey: ["customers-by-user", userId],
    queryFn: () => api.getCustomers({ user: userId, limit: 50 }, token!) as Promise<{ data: ICustomer[]; count: number }>,
    enabled: !!token && !!userId,
  })

  const isLoading = isLoadingUser || isLoadingCustomers
  const customers = customersData?.data || []
  const customer = customers[0]

  if (isLoading) {
    return <div className="flex items-center justify-center py-20 text-sm text-gray-500">Caricamento...</div>
  }

  if (!appUser) {
    return <div className="flex items-center justify-center py-20 text-sm text-gray-500">Utente non trovato</div>
  }

  const displayName = customer
    ? `${customer.surname || ""} ${customer.name || ""}`.trim()
    : appUser.displayName || appUser.email

  const allCustomerCodes = customers.map((c) => c.customerCode)
  const subtitle = customers.length > 0
    ? `${customers.length > 1 ? "Codici cliente" : "Codice cliente"}: ${allCustomerCodes.join(", ")} · CF: ${customer?.fiscalCode || ""}`
    : appUser.email

  const tabs: { key: Tab; label: string; icon: typeof User }[] = [
    { key: "account-loyalty", label: "Account Loyalty", icon: CreditCard },
    { key: "contracts", label: "Contratti", icon: FileText },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate("/customers")} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{displayName}</h1>
          <p className="mt-0.5 text-sm text-gray-500">{subtitle}</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
        {tabs.map((tab) => {
          const Icon = tab.icon
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              <Icon className="h-4 w-4" />
              {tab.label}
            </button>
          )
        })}
      </div>

      {/* Tab Content */}
      {activeTab === "account-loyalty" && <AccountLoyaltyTab customer={customer} appUser={appUser} userId={userId!} token={token!} />}
      {activeTab === "contracts" && <ContractsTab customerCodes={allCustomerCodes} token={token!} />}
    </div>
  )
}

// ============================================================================
// Account Loyalty Tab (merged Account + Fidelity — 2/3 fidelity, 1/3 profile)
// ============================================================================

function AccountLoyaltyTab({ customer, appUser, userId, token }: { customer?: ICustomer; appUser: Record<string, any>; userId: string; token: string }) {
  const { canResetPassword } = usePermissions()
  const [showResetPw, setShowResetPw] = useState(false)
  const [newPassword, setNewPassword] = useState("")
  const [isResetting, setIsResetting] = useState(false)

  async function handleResetPassword(e: React.FormEvent) {
    e.preventDefault()
    setIsResetting(true)
    try {
      await api.updateUser(userId, { password: newPassword }, token)
      toast.success("Password reimpostata")
      setShowResetPw(false)
      setNewPassword("")
    } catch {
      toast.error("Errore nel reset della password")
    } finally {
      setIsResetting(false)
    }
  }

  // User account fields
  const userFields = [
    { label: "Email", value: appUser.email },
    { label: "Nome utente", value: appUser.displayName },
    { label: "Ruolo", value: appUser.role },
    { label: "Registrato il", value: appUser.createdAt ? new Date(appUser.createdAt).toLocaleDateString("it-IT") : undefined },
  ]

  // Customer profile fields (if linked)
  const customerFields = customer ? [
    { label: "Codice cliente", value: String(customer.customerCode) },
    { label: "Nome", value: customer.name },
    { label: "Cognome", value: customer.surname },
    { label: "Codice Fiscale", value: customer.fiscalCode },
    { label: "Telefono", value: customer.phone },
    { label: "Data di nascita", value: customer.birthDate ? new Date(customer.birthDate).toLocaleDateString("it-IT") : undefined },
    { label: "Indirizzo", value: [customer.address?.street, customer.address?.city, customer.address?.province].filter(Boolean).join(", ") || undefined },
    { label: "CAP", value: (customer.address as any)?.zipCode },
  ] : []

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {/* LEFT: Fidelity (2/3) */}
      <div className="space-y-6 lg:col-span-2">
        <FidelityTab customerId={customer?.id} userId={userId} token={token} />
      </div>

      {/* RIGHT: Profile + Password (1/3) */}
      <div className="space-y-6">
        {/* Account App */}
        <div className="rounded-xl border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-5 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Account App</h3>
          </div>
          <div className="space-y-3 p-5">
            {userFields.map((f) => (
              <div key={f.label}>
                <p className="text-xs font-medium uppercase text-gray-500">{f.label}</p>
                <p className="mt-0.5 text-sm text-gray-900">{f.value || "-"}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Customer Profile */}
        {customerFields.length > 0 && (
          <div className="rounded-xl border border-gray-200 bg-white">
            <div className="border-b border-gray-200 px-5 py-3">
              <h3 className="text-sm font-semibold text-gray-900">Profilo Cliente</h3>
            </div>
            <div className="space-y-3 p-5">
              {customerFields.map((f) => (
                <div key={f.label}>
                  <p className="text-xs font-medium uppercase text-gray-500">{f.label}</p>
                  <p className="mt-0.5 text-sm text-gray-900">{f.value || "-"}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Password Reset */}
        {canResetPassword && (
        <div className="rounded-xl border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-5 py-3">
            <h3 className="text-sm font-semibold text-gray-900">Sicurezza</h3>
          </div>
          <div className="p-5">
            {!showResetPw ? (
              <button
                onClick={() => setShowResetPw(true)}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <KeyRound className="h-4 w-4" />
                Reimposta password
              </button>
          ) : (
              <form onSubmit={handleResetPassword} className="space-y-3">
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-gray-700">Nuova password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    placeholder="Minimo 8 caratteri"
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </div>
                <div className="flex gap-2">
                  <button type="submit" disabled={isResetting} className="flex-1 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                    {isResetting ? "..." : "Reimposta"}
                  </button>
                  <button type="button" onClick={() => { setShowResetPw(false); setNewPassword("") }} className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                    Annulla
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Fidelity Tab
// ============================================================================

interface LoyaltyRuleOption {
  code: string
  label: string
  points: number
  category: string
  active: boolean
}

const MANUAL_RULE_CODE = "__MANUAL__"

function FidelityTab({ customerId, userId, token }: { customerId?: string; userId: string; token: string }) {
  if (!customerId) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
        <CreditCard className="mx-auto h-10 w-10 text-gray-300" />
        <p className="mt-3 text-sm text-gray-500">Nessun profilo cliente collegato</p>
      </div>
    )
  }
  const queryClient = useQueryClient()
  const { canWritePoints } = usePermissions()
  const [showAddPoints, setShowAddPoints] = useState(false)
  const [showRedeemReward, setShowRedeemReward] = useState(false)
  const [selectedRuleCode, setSelectedRuleCode] = useState(MANUAL_RULE_CODE)
  const [pointsAmount, setPointsAmount] = useState("")
  const [pointsDescription, setPointsDescription] = useState("")
  const [deletingTx, setDeletingTx] = useState<ILoyaltyTransaction | null>(null)

  // Fetch loyalty rules for the dropdown
  const { data: rulesData } = useQuery({
    queryKey: ["loyalty-rules"],
    queryFn: () => api.getLoyaltyRules(token) as Promise<LoyaltyRuleOption[]>,
    enabled: !!token,
  })

  const activeRules = (rulesData || []).filter((r) => r.active && r.category !== "redeem")

  // Fetch the loyalty card via the user (a fiscal code may span multiple
  // customer codes that all share a single card).
  const { data: cardsData } = useQuery({
    queryKey: ["loyalty-cards", userId],
    queryFn: () => api.getLoyaltyCards({ user: userId }, token) as Promise<{ data: ILoyaltyCard[] }>,
    enabled: !!token && !!userId,
  })

  const card = (cardsData?.data || cardsData as unknown as ILoyaltyCard[] || [])?.[0] as ILoyaltyCard | undefined

  // Fetch transactions if card exists
  const { data: txData } = useQuery({
    queryKey: ["loyalty-transactions", card?.id],
    queryFn: () => api.getCardTransactions(card!.id, { limit: 50 }, token),
    enabled: !!card?.id,
  })

  const transactions = (txData as any)?.data || txData || []

  // Adjust points mutation
  const adjustMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.adjustPoints(card!.id, body, token),
    onSuccess: () => {
      toast.success("Punti aggiornati")
      queryClient.invalidateQueries({ queryKey: ["loyalty-cards", userId] })
      queryClient.invalidateQueries({ queryKey: ["loyalty-transactions", card?.id] })
      resetForm()
    },
    onError: () => toast.error("Errore nell'aggiornamento punti"),
  })

  // Recalculate balance mutation
  const recalcMutation = useMutation({
    mutationFn: () => api.recalculateBalance(card!.id, token),
    onSuccess: () => {
      toast.success("Saldo ricalcolato")
      queryClient.invalidateQueries({ queryKey: ["loyalty-cards", userId] })
      queryClient.invalidateQueries({ queryKey: ["loyalty-transactions", card?.id] })
    },
    onError: () => toast.error("Errore nel ricalcolo"),
  })

  function resetForm() {
    setShowAddPoints(false)
    setSelectedRuleCode(MANUAL_RULE_CODE)
    setPointsAmount("")
    setPointsDescription("")
  }

  function handleRuleChange(code: string) {
    setSelectedRuleCode(code)
    if (code !== MANUAL_RULE_CODE) {
      const rule = activeRules.find((r) => r.code === code)
      if (rule) {
        setPointsAmount(String(rule.points))
      }
    } else {
      setPointsAmount("")
    }
  }

  function handleAddPoints(e: React.FormEvent) {
    e.preventDefault()
    const pts = parseInt(pointsAmount)
    if (isNaN(pts) || pts === 0) return

    const isManual = selectedRuleCode === MANUAL_RULE_CODE
    const selectedRule = !isManual ? activeRules.find((r) => r.code === selectedRuleCode) : null

    adjustMutation.mutate({
      points: pts,
      description: pointsDescription.trim(),
      ruleCode: isManual ? "MANUAL_ADJUSTMENT" : selectedRule?.code || "MANUAL_ADJUSTMENT",
      ruleLabel: isManual
        ? (pts > 0 ? "Rettifica manuale (+)" : "Rettifica manuale (-)")
        : selectedRule?.label || "Rettifica manuale",
    })
  }

  if (!card) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
        <CreditCard className="mx-auto h-10 w-10 text-gray-300" />
        <p className="mt-3 text-sm text-gray-500">Nessuna fidelity card associata</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Card Summary */}
      <div className="rounded-xl border border-gray-200 bg-white">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h3 className="text-base font-semibold text-gray-900">Fidelity Card</h3>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${card.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
            {card.status}
          </span>
        </div>
        <div className="grid grid-cols-2 gap-4 p-6">
          <div>
            <p className="text-xs font-medium uppercase text-gray-500">Saldo Punti</p>
            <p className="mt-1 text-2xl font-bold text-primary">{card.balance}</p>
          </div>
          <div>
            <p className="text-xs font-medium uppercase text-gray-500">Codice Referral</p>
            <p className="mt-1 text-sm font-mono text-gray-900">{card.referralCode || "-"}</p>
          </div>
        </div>
        {canWritePoints && (
        <div className="flex flex-wrap gap-2 border-t border-gray-200 px-6 py-3">
          <button
            onClick={() => setShowAddPoints(true)}
            className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> Movimenta
          </button>
          <button
            onClick={() => setShowRedeemReward(true)}
            className="flex items-center gap-1.5 rounded-lg border border-primary px-3 py-2 text-sm font-medium text-primary hover:bg-primary/5"
          >
            <Gift className="h-4 w-4" /> Riscatta
          </button>
          <button
            onClick={() => recalcMutation.mutate()}
            disabled={recalcMutation.isPending}
            className="rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Ricalcola
          </button>
        </div>
        )}
      </div>

      {/* Add Points Form */}
      {showAddPoints && canWritePoints && (
        <div className="rounded-xl border border-primary/20 bg-primary/5 p-5">
          <form onSubmit={handleAddPoints} className="space-y-4">
            {/* Row 1: Rule selector + Points */}
            <div className="flex flex-wrap gap-3">
              <div className="flex-1 space-y-1.5" style={{ minWidth: "220px" }}>
                <label className="block text-sm font-medium text-gray-700">Tipo movimento</label>
                <select
                  value={selectedRuleCode}
                  onChange={(e) => handleRuleChange(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                >
                  <option value={MANUAL_RULE_CODE}>✏️ Rettifica manuale (testo libero)</option>
                  <optgroup label="Regole accumulo">
                    {activeRules.filter((r) => r.category === "earn").map((rule) => (
                      <option key={rule.code} value={rule.code}>
                        {rule.label} ({rule.points > 0 ? "+" : ""}{rule.points} pt)
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="Rettifiche">
                    {activeRules.filter((r) => r.category === "adjustment").map((rule) => (
                      <option key={rule.code} value={rule.code}>
                        {rule.label}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700">
                  Punti {selectedRuleCode !== MANUAL_RULE_CODE && <span className="text-xs font-normal text-gray-400">(sovrascrivibile)</span>}
                </label>
                <input
                  type="number"
                  value={pointsAmount}
                  onChange={(e) => setPointsAmount(e.target.value)}
                  required
                  placeholder="es. 100 o -50"
                  className="w-32 rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            {/* Row 2: Description */}
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                Causale {selectedRuleCode === MANUAL_RULE_CODE && <span className="text-red-500">*</span>}
                {selectedRuleCode !== MANUAL_RULE_CODE && <span className="text-xs font-normal text-gray-400"> (opzionale — la label della regola sarà usata come default)</span>}
              </label>
              <input
                type="text"
                value={pointsDescription}
                onChange={(e) => setPointsDescription(e.target.value)}
                required={selectedRuleCode === MANUAL_RULE_CODE}
                placeholder={selectedRuleCode === MANUAL_RULE_CODE ? "Motivo della movimentazione (visibile in app)" : "Note aggiuntive (opzionale)"}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </div>

            {/* Row 3: Actions */}
            <div className="flex gap-3">
              <button type="submit" disabled={adjustMutation.isPending} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                {adjustMutation.isPending ? "Invio..." : "Conferma"}
              </button>
              <button type="button" onClick={resetForm} className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                Annulla
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Redeem Reward Dialog */}
      {showRedeemReward && canWritePoints && (
        <AdminRedeemDialog
          customerId={customerId}
          token={token}
          cardBalance={card.balance}
          onClose={() => setShowRedeemReward(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["loyalty-cards", userId] })
            queryClient.invalidateQueries({ queryKey: ["loyalty-transactions", card?.id] })
            setShowRedeemReward(false)
          }}
        />
      )}

      {/* Transactions Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-base font-semibold text-gray-900">Movimentazioni</h3>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Data</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Descrizione</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Fonte</th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Punti</th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Saldo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {(transactions as ILoyaltyTransaction[]).length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">Nessuna movimentazione</td></tr>
            ) : (
              (transactions as ILoyaltyTransaction[]).map((tx) => (
                <tr key={tx.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm text-gray-600">{new Date(tx.createdAt).toLocaleDateString("it-IT")}</td>
                  <td className="px-6 py-3 text-sm text-gray-900">{tx.ruleLabel}{tx.description ? ` — ${tx.description}` : ""}</td>
                  <td className="px-6 py-3">
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">{tx.source}</span>
                  </td>
                  <td className={`px-6 py-3 text-right text-sm font-medium ${tx.points > 0 ? "text-green-600" : "text-red-600"}`}>
                    {tx.points > 0 ? "+" : ""}{tx.points}
                  </td>
                  <td className="px-6 py-3 text-right text-sm text-gray-600">{tx.balanceAfter}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ============================================================================
// Admin Redeem Dialog
// ============================================================================

function AdminRedeemDialog({ customerId, token, cardBalance, onClose, onSuccess }: {
  customerId: string
  token: string
  cardBalance: number
  onClose: () => void
  onSuccess: () => void
}) {
  const { data: rewardsData, isLoading } = useQuery({
    queryKey: ["rewards-active"],
    queryFn: () => api.getRewards({ active: true, limit: 50 }, token) as Promise<{ data: any[] }>,
    enabled: !!token,
  })

  const rewards = (rewardsData?.data || []).filter((r: any) => r.pointsCost <= cardBalance)
  const unavailable = (rewardsData?.data || []).filter((r: any) => r.pointsCost > cardBalance)

  const redeemMutation = useMutation({
    mutationFn: (rewardId: string) => api.adminRedeemReward(rewardId, customerId, token),
    onSuccess: () => {
      toast.success("Premio riscattato")
      onSuccess()
    },
    onError: () => toast.error("Errore nel riscatto"),
  })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-900">Riscatta premio per il cliente</h3>
          <p className="mt-0.5 text-sm text-gray-500">Saldo disponibile: <span className="font-semibold text-primary">{cardBalance} punti</span></p>
        </div>
        <div className="max-h-[400px] overflow-y-auto p-4">
          {isLoading ? (
            <p className="py-8 text-center text-sm text-gray-500">Caricamento premi...</p>
          ) : rewards.length === 0 && unavailable.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-500">Nessun premio disponibile</p>
          ) : (
            <div className="space-y-2">
              {rewards.map((r: any) => (
                <div key={r.id} className="flex items-center justify-between rounded-lg border border-gray-200 p-3 hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    <Gift className="h-5 w-5 text-primary" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{r.name}</p>
                      <p className="text-xs text-gray-500">{r.pointsCost} punti</p>
                    </div>
                  </div>
                  <button
                    onClick={() => redeemMutation.mutate(r.id)}
                    disabled={redeemMutation.isPending}
                    className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    Riscatta
                  </button>
                </div>
              ))}
              {unavailable.length > 0 && (
                <>
                  <p className="pt-3 text-xs font-medium uppercase text-gray-400">Punti insufficienti</p>
                  {unavailable.map((r: any) => (
                    <div key={r.id} className="flex items-center justify-between rounded-lg border border-gray-100 p-3 opacity-40">
                      <div className="flex items-center gap-3">
                        <Gift className="h-5 w-5 text-gray-300" />
                        <div>
                          <p className="text-sm font-medium text-gray-600">{r.name}</p>
                          <p className="text-xs text-gray-400">{r.pointsCost} punti</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
        <div className="border-t border-gray-200 px-6 py-3">
          <button onClick={onClose} className="w-full rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Chiudi
          </button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Contracts Tab
// ============================================================================

function ContractsTab({ customerCodes, token }: { customerCodes: number[]; token: string }) {
  if (!customerCodes.length) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-8 text-center">
        <FileText className="mx-auto h-10 w-10 text-gray-300" />
        <p className="mt-3 text-sm text-gray-500">Nessun profilo cliente collegato</p>
      </div>
    )
  }
  const queryClient = useQueryClient()
  const { canEditContracts } = usePermissions()
  const [editingContract, setEditingContract] = useState<IContract | null>(null)
  const [startDate, setStartDate] = useState("")
  const [endDate, setEndDate] = useState("")

  // Fetch contracts for ALL customer codes
  const { data: contractsData } = useQuery({
    queryKey: ["contracts", ...customerCodes],
    queryFn: async () => {
      const results = await Promise.all(
        customerCodes.map((code) => api.getContracts({ customerCode: code }, token) as Promise<any>)
      )
      const allContracts = results.flatMap((r) => r?.data || r || [])
      return allContracts
    },
    enabled: !!token,
  })

  const contracts = (contractsData || []) as IContract[]

  const updateMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.updateContract(id, body, token),
    onSuccess: () => {
      toast.success("Contratto aggiornato")
      queryClient.invalidateQueries({ queryKey: ["contracts"] })
      setEditingContract(null)
    },
    onError: () => toast.error("Errore nell'aggiornamento"),
  })

  function openEdit(contract: IContract) {
    setEditingContract(contract)
    setStartDate(contract.startDate ? contract.startDate.split("T")[0] : "")
    setEndDate(contract.endDate ? contract.endDate.split("T")[0] : "")
  }

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    if (!editingContract) return
    updateMutation.mutate({
      id: editingContract.id,
      body: { startDate, endDate },
    })
  }

  return (
    <div className="space-y-6">
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 className="text-base font-semibold text-gray-900">Contratti Attivi</h3>
        </div>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Codice</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Commodity</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Stato</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Inizio</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Scadenza</th>
              {canEditContracts && <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Azioni</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {contracts.length === 0 ? (
              <tr><td colSpan={6} className="px-6 py-8 text-center text-sm text-gray-500">Nessun contratto</td></tr>
            ) : (
              contracts.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="px-6 py-3 text-sm font-mono font-medium text-gray-900">{c.code}</td>
                  <td className="px-6 py-3 text-sm text-gray-600">{c.commodity || "-"}</td>
                  <td className="px-6 py-3">
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${c.status === "open" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-sm text-gray-600">{c.startDate ? new Date(c.startDate).toLocaleDateString("it-IT") : "-"}</td>
                  <td className="px-6 py-3 text-sm text-gray-600">{c.endDate ? new Date(c.endDate).toLocaleDateString("it-IT") : "-"}</td>
                  {canEditContracts && (
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => openEdit(c)} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                      <Pencil className="h-4 w-4" />
                    </button>
                  </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Contract Dialog */}
      {editingContract && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Modifica Date Contratto</h3>
            <p className="mt-1 text-sm text-gray-500">Contratto: {editingContract.code}</p>
            <form onSubmit={handleSave} className="mt-4 space-y-4">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700">Data Inizio</label>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
              </div>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700">Data Scadenza</label>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} required className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setEditingContract(null)} className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Annulla</button>
                <button type="submit" disabled={updateMutation.isPending} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                  {updateMutation.isPending ? "Salvataggio..." : "Salva"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
